"use client"

import { useEffect, useState } from "react"
import { X } from "lucide-react"
import { useAuthFlow } from "@/lib/auth-flow-context"

export function UpdateNotification() {
  const [showUpdate, setShowUpdate] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [pendingAutoUpdate, setPendingAutoUpdate] = useState(false)
  const [updateHandled, setUpdateHandled] = useState(false)
  const { isInCriticalFlow } = useAuthFlow()

  useEffect(() => {
    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    setIsIOS(iOS)

    // Listen for update available event
    const handleUpdateAvailable = () => {
      // Ignore duplicate events - only handle once per session
      if (updateHandled) {
        console.log("Update notification already handled, ignoring duplicate event")
        return
      }

      setUpdateHandled(true)

      if (isInCriticalFlow) {
        // Hide notification during auth, mark for auto-update
        setPendingAutoUpdate(true)
        console.log("Update available during auth - will auto-apply after completion")
      } else {
        setShowUpdate(true)
      }
    }

    window.addEventListener("sw-update-available", handleUpdateAvailable)

    return () => {
      window.removeEventListener("sw-update-available", handleUpdateAvailable)
    }
  }, [isInCriticalFlow, updateHandled])

  // Automatically reload when auth completes and update is pending
  // BUT: Skip if we're on an auth page - Clerk will handle the redirect
  //      and the new service worker will activate on the next page load
  useEffect(() => {
    if (pendingAutoUpdate && !isInCriticalFlow) {
      const isAuthPage = typeof window !== 'undefined' && (
        window.location.pathname.includes('/sign-in') ||
        window.location.pathname.includes('/sign-up') ||
        window.location.pathname.includes('/reset-password') ||
        window.location.pathname.includes('/forgot-password')
      )

      if (isAuthPage) {
        console.log("Auth completed on auth page - skipping auto-reload, Clerk will redirect")
        // Clear the pending update flag but don't reload
        // The redirect will load the new version naturally
        setPendingAutoUpdate(false)
      } else {
        console.log("Auth completed - auto-applying update")
        // Small delay to ensure auth is fully complete
        setTimeout(() => {
          window.location.reload()
        }, 500)
      }
    }
  }, [isInCriticalFlow, pendingAutoUpdate])

  const handleUpdate = () => {
    setShowUpdate(false)
    window.location.reload()
  }

  const handleDismiss = () => {
    setShowUpdate(false)
    // Show again in 5 minutes if they dismiss
    setTimeout(() => setShowUpdate(true), 5 * 60 * 1000)
  }

  // Don't show notification during auth
  if (!showUpdate || isInCriticalFlow) return null

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-in slide-in-from-bottom-5 sm:left-auto sm:right-4 sm:max-w-md">
      <div className="rounded-lg border border-border bg-background p-4 shadow-lg">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className="font-semibold text-sm mb-1">
              Nová verze je dostupná
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              Aktualizuj aplikaci, abys získal/a nejnovější vylepšení.
            </p>
            {isIOS && (
              <p className="text-xs text-muted-foreground mb-3 italic">
                iOS tip: Pro nejlepší výsledky úplně zavři aplikaci a znovu ji otevři.
              </p>
            )}
            <button
              onClick={handleUpdate}
              className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Aktualizovat nyní
            </button>
          </div>
          <button
            onClick={handleDismiss}
            className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Zavřít"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
